import {
  Injectable,
  Inject,
  Renderer2,
  RendererFactory2,
  PLATFORM_ID,
} from "@angular/core";
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from "@angular/common";
import {
  Resolve,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from "@angular/router";
import {
  Meta,
  Title,
  TransferState,
  makeStateKey,
  StateKey,
} from "@angular/platform-browser";
import { Observable, of } from "rxjs";
import { tap, switchMap } from "rxjs/operators";

import { GeneralService } from "app/services/general.service";
import { Machine } from "app/shared/machine";

@Injectable({ providedIn: "root" })
export class MachineResolver implements Resolve<Machine | null> {
  private renderer: Renderer2;

  constructor(
    private machineService: GeneralService,
    private meta: Meta,
    private title: Title,
    private transferState: TransferState,
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<Machine | null> {
    const langRaw = route.paramMap.get("lang");
    const id = route.paramMap.get("id") || "";
    const lang = this.normalizeLang(langRaw); // EN/AR/FR/TR/RU

    const TS_KEY: StateKey<Machine | null> = makeStateKey<Machine | null>(
      `machine_${id}_${lang}`
    );

    // SSR: جب جديد دائمًا
    if (isPlatformServer(this.platformId)) {
      return this.fetchAndApply(id, lang, TS_KEY, state.url);
    }

    // Browser: جرّب TransferState أولًا
    if (this.transferState.hasKey(TS_KEY)) {
      const cached = this.transferState.get(TS_KEY, null);
      this.transferState.remove(TS_KEY);
      if (cached && (cached as any)._id === id) {
        this.applyMeta(cached, lang, state.url);
        return of(cached);
      }
    }

    // Otherwise fetch
    return this.fetchAndApply(id, lang, TS_KEY, state.url);
  }

  // -------- Helpers --------

  private fetchAndApply(
    id: string,
    lang: LangKey,
    key: StateKey<Machine | null>,
    url: string
  ) {
    return this.machineService.getItem("machine/getMachineByName", id).pipe(
      tap((data: Machine | null) => {
        if (isPlatformServer(this.platformId)) {
          this.transferState.set(key, data || null);
        }
        this.applyMeta(data || null, lang, url);
      }),
      switchMap((data) => of(data || null))
    );
  }

  /** EN/AR/FR/TR/RU (default EN) */
  private normalizeLang(lang: string | null): LangKey {
    const L = (lang || "en").toUpperCase();
    const allowed: LangKey[] = ["EN", "AR", "FR", "TR", "RU"];
    return (allowed.includes(L as LangKey) ? L : "EN") as LangKey;
  }

  private pick<T extends Record<string, string> | undefined>(
    obj: T,
    lang: LangKey,
    fallbacks: LangKey[] = ["EN"]
  ): string | undefined {
    if (!obj) return undefined;
    if (obj[lang]) return obj[lang];
    for (const fb of fallbacks) if (obj[fb]) return obj[fb];
    const keys = Object.keys(obj);
    return keys.length ? (obj as any)[keys[0]] : undefined;
  }

  /** نظّف أي HTML من الوصف */
  private stripHtml(html: string | undefined): string {
    if (!html) return "";
    if (isPlatformBrowser(this.platformId)) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
    }
    return html
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  /** اختَر صورة OG مناسبة */
  private pickOgImage(machine: Machine | null | undefined): string | undefined {
    const dists = (machine as any)?.machineDists as
      | { url?: string }[]
      | undefined;
    const imgs = (machine as any)?.images as { url?: string }[] | undefined;
    return dists?.[0]?.url || imgs?.[0]?.url;
  }

  /** حوّل أي URL إلى مطلق */
  private toAbsolute(u: string | undefined): string | undefined {
    if (!u) return u;
    try {
      // مطلق؟
      new URL(u);
      return u;
    } catch {
      return this.absUrl(u);
    }
  }

  /** استخرج ID يوتيوب من رابط كامل أو مرّره كما هو إن كان ID */
  private extractYouTubeId(input: string): string {
    // أمثلة: https://www.youtube.com/watch?v=ID  |  https://youtu.be/ID  |  ID
    const re =
      /(?:v=|\/embed\/|youtu\.be\/|\/shorts\/)([A-Za-z0-9_-]{6,})|^([A-Za-z0-9_-]{6,})$/;
    const m = input.match(re);
    return (m?.[1] || m?.[2] || input).trim();
  }

  /** طبّق العناوين والميتا */
  private applyMeta(machine: Machine | null, lang: LangKey, url: string) {
    if (!machine) return;

    // ---- تنظيف مسبق لأي وسم خاطئ ----
    this.meta.removeTag('property="description"'); // يزيل <meta property="description">

    // 1) Meta Title = metaTag[lang] (fallback الاسم)
    const metaTitle =
      this.pick(machine.metaTag as any, lang) ||
      this.pick(machine.name as any, lang) ||
      "Product";

    // ✅ 2) عنوان الصفحة = الـ Meta Title (كما طلبت)
    const pageTitle = metaTitle;

    // 3) Meta Description = metaDescription[lang] (fallback smallDes، ثم العنوان)
    const metaDescription =
      this.stripHtml(
        this.pick(machine.metaDescription as any, lang) ||
          this.pick(machine.smallDes as any, lang) ||
          ""
      ) || pageTitle;

    // 4) صورة OG (مطلقة)
    const ogImage = this.toAbsolute(
      this.pickOgImage(machine) ||
        "https://msr.zirvecompany.net/images/logo30.png"
    )!;

    // 5) URL مطلق
    const abs = this.absUrl(url);

    // ---- تطبيق العنوان والميتا مع selectors تمنع التكرار ----
    this.title.setTitle(pageTitle);

    // description (name=description)
    this.meta.updateTag(
      { name: "description", content: metaDescription },
      'name="description"'
    );

    // Open Graph
    this.meta.updateTag(
      { property: "og:title", content: metaTitle },
      'property="og:title"'
    );
    this.meta.updateTag(
      { property: "og:description", content: metaDescription },
      'property="og:description"'
    );
    this.meta.updateTag(
      { property: "og:type", content: "product" },
      'property="og:type"'
    );
    this.meta.updateTag(
      { property: "og:image", content: ogImage },
      'property="og:image"'
    );
    this.meta.updateTag(
      { property: "og:url", content: abs },
      'property="og:url"'
    );

    // Twitter
    this.meta.updateTag(
      { name: "twitter:card", content: "summary_large_image" },
      'name="twitter:card"'
    );
    this.meta.updateTag(
      { name: "twitter:title", content: metaTitle },
      'name="twitter:title"'
    );
    this.meta.updateTag(
      { name: "twitter:description", content: metaDescription },
      'name="twitter:description"'
    );
    this.meta.updateTag(
      { name: "twitter:image", content: ogImage },
      'name="twitter:image"'
    );

    // (اختياري) locale
    const ogLocale =
      lang === "AR"
        ? "ar_AR"
        : lang === "TR"
        ? "tr_TR"
        : lang === "FR"
        ? "fr_FR"
        : lang === "RU"
        ? "ru_RU"
        : "en_US";
    this.meta.updateTag(
      { property: "og:locale", content: ogLocale },
      'property="og:locale"'
    );

    // (اختياري) robots
    this.meta.updateTag(
      { name: "robots", content: "index,follow" },
      'name="robots"'
    );

    // (اختياري) JSON-LD للفيديو إن وُجد
    const rawVideo = (machine as any)?.youTubeVideos?.[0]?.url;
    if (rawVideo) {
      const id = this.extractYouTubeId(rawVideo);
      const embedUrl = `https://www.youtube.com/embed/${id}`;
      const thumbUrl = `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
      this.addVideoSchema({
        name: pageTitle,
        description: metaDescription,
        thumbnailUrl: thumbUrl,
        embedUrl,
        uploadDate: (machine as any)?.createdAt,
        duration: "PT0H9M51S",
        regionsAllowed: ["IN"],
      });
    }
  }

  private absUrl(path: string): string {
    try {
      const base = isPlatformBrowser(this.platformId)
        ? window.location.origin
        : "https://www.zirvecompany.net";
      return new URL(path || "/", base).toString();
    } catch {
      return path || "/";
    }
  }

  private addVideoSchema(opts: {
    name: string;
    description: string;
    thumbnailUrl: string;
    embedUrl: string;
    uploadDate: string;
    duration: string;
    regionsAllowed: string[];
  }) {
    const id = "video-schema";
    const existing = this.document.getElementById(id);
    if (existing) this.renderer.removeChild(this.document.head, existing);

    const script = this.renderer.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    script.text = JSON.stringify(
      {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: opts.name,
        description: opts.description,
        thumbnailUrl: [opts.thumbnailUrl],
        uploadDate: opts.uploadDate,
        duration: opts.duration,
        embedUrl: opts.embedUrl,
        regionsAllowed: opts.regionsAllowed,
      },
      null,
      2
    );
    this.renderer.appendChild(this.document.head, script);
  }
}

/** نوع لمفاتيح اللغات المخزّنة لديك */
type LangKey = "EN" | "AR" | "FR" | "TR" | "RU";
